import { Button, DateInput, Modal, useMobile } from '@octano/global-ui';
import dayjs from 'dayjs';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import { useValidations } from '../../../../../hooks/useValidations';

const getYesterdaysDate = () => dayjs().subtract(1, 'd').toDate();

const DEFAULT_VALUES = {
  initDate: dayjs(getYesterdaysDate()).date(1).toDate(),
  endDate: getYesterdaysDate(),
};

type DateStrings = { initDate: string; endDate: string };

interface Props {
  isOpen: boolean;
  onConfirm?: (dateValues: DateStrings) => void;
  onCancel?: () => void;
  validationMessage: string | null;
}

export default function SelectDatesModal({
  isOpen,
  onConfirm = () => null,
  onCancel = () => null,
  validationMessage,
}: Props) {
  const { t } = useTranslation();
  const prefix = 'reports.classExecution.datesConfig';

  const isMobile = useMobile();

  const { msgValidations } = useValidations();
  const { control, watch, handleSubmit } = useForm({
    defaultValues: DEFAULT_VALUES,
    shouldUnregister: true,
  });

  const { initDate } = watch();

  const handleConfirm = useCallback(
    async (values) => {
      onConfirm({
        initDate: dayjs(values.initDate).format('YYYY-MM-DD'),
        endDate: dayjs(values.endDate).format('YYYY-MM-DD'),
      });
    },
    [onConfirm],
  );

  return (
    <Modal isOpen={isOpen} toggle={() => null}>
      <Form onSubmit={handleSubmit(handleConfirm)}>
        <h1 className="text-dark text-center fw-600 fs-20">
          {t(`${prefix}.title`)}
        </h1>
        <p className=" text-center fs-16 lh-30 mt-4 mb-5">
          {t(`${prefix}.subtitle`)}
        </p>
        <Row>
          <Col lg="6" md="6" sm="12">
            <DateInput
              name="initDate"
              label={t(`common.forms.dateFrom`)}
              control={control}
              maxDate={getYesterdaysDate()}
              rules={{
                required: msgValidations.required,
              }}
            />
          </Col>
          <Col lg="6" md="6" sm="12">
            <DateInput
              name="endDate"
              label={t(`common.forms.dateTo`)}
              control={control}
              minDate={initDate}
              maxDate={getYesterdaysDate()}
              rules={{
                required: msgValidations.required,
              }}
            />
          </Col>
          {validationMessage && (
            <Col xs="12">
              <p className="text-danger mt-4">{validationMessage}</p>
            </Col>
          )}
        </Row>
        <div
          className="d-flex flex-wrap justify-content-between mt-5"
          style={{ gap: '1rem' }}
        >
          <Button
            text={t(`common.actions.cancel`)}
            outlined
            style={{ width: isMobile ? '100%' : 197 }}
            onClick={onCancel}
          />
          <Button
            type="submit"
            text={t(`common.actions.download`)}
            style={{ width: isMobile ? '100%' : 197 }}
          />
        </div>
      </Form>
    </Modal>
  );
}
